import type { ErrorInfo } from 'react';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Box } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import type { Theme, WithStyles } from '@material-ui/core/styles';

import { StackdriverLogger } from '@klpkonsern/stackdriver-logger';
import type { IAppDispatch, IAppState } from '@klp/core/app';
import { appDispatch, appSelectors } from '@klp/core/app';
import {
    Block,
    Heading,
    Typography,
    ActionButton,
    KlpStyles,
} from '@klp/design';
import { Main } from '@klp/layout/Main';

const ErrorStyles = (theme: Theme) => ({
    errorContainer: {
        height: '100%',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flexDirection: 'column',
    },
    errorContent: {
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        minHeight: '50%',
        gap: theme.spacing(1),
    },
});

export type IErrorBoundaryProps = WithStyles & IAppState & IAppDispatch;

class ErrorBoundaryClass extends Component<IErrorBoundaryProps> {
    static displayName = 'ErrorBoundary';

    state = {
        hasError: false,
    };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        const structuredError = {
            name: error?.name,
            message: error.message,
            stack: error.stack,
            componentStack: errorInfo.componentStack,
            errorType: 'Unhandled Application Error',
        };

        StackdriverLogger.log(structuredError);

        console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    render() {
        const { classes, error, children } = this.props;

        if (this.state.hasError || error) {
            return (
                <Main fullScreen={true}>
                    <Block className={classes.errorContainer}>
                        <Box className={classes.errorContent}>
                            <Heading klpHeadingLevel="ONE"> Oops! </Heading>
                            <Typography
                                variant="h5"
                                translation="general.errorMessage"
                            />
                            <ActionButton
                                actionType="button"
                                translation="general.reload"
                                onClick={() => window.location.reload()}
                            />
                        </Box>
                    </Block>
                </Main>
            );
        }

        return children;
    }
}

const enhance = compose(
    connect(appSelectors, appDispatch),
    withStyles(KlpStyles(ErrorStyles)),
);
export const ErrorHandler = enhance(ErrorBoundaryClass);
